import React, { useState } from "react";
import { ScrollText, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    heading: "Acceptance of Terms",
    text: "By accessing or using the RallyPointGo system, users agree to be bound by these Terms of Use. If a user does not agree to these terms, they must discontinue use of the system immediately.",
  },
  {
    heading: "Authorized Use",
    text: "RallyPointGo is intended solely for use by coaches, directors, team members, and authorized personnel associated with approved programs. Users may only access the system using their assigned login credentials or through approved enrollment workflows. Unauthorized access, account sharing, or attempts to bypass system controls are strictly prohibited.",
  },
  {
    heading: "User Responsibilities",
    intro: "Users agree to:",
    bullets: [
      "Provide accurate, truthful, and complete information when creating or updating their profile.",
      "Maintain the confidentiality of their login credentials.",
      "Use the system in a manner consistent with team policies, school policies, and applicable laws.",
      "Verify the accuracy of any information displayed within the system before acting upon it.",
      "Ensure their device settings allow proper operation of notifications and communication features.",
    ],
    outro: "Users are solely responsible for all activity conducted under their account.",
  },
  {
    heading: "System Content and Accuracy",
    text: "RallyPointGo provides tools for communication, scheduling, merit tracking, and team management. While reasonable efforts are made to maintain accurate and timely information, RallyPointGo does not guarantee completeness, accuracy, or uninterrupted availability of any data, notifications, or system features.",
  },
  {
    heading: "Prohibited Conduct",
    intro: "Users may not:",
    bullets: [
      "Attempt to gain unauthorized access to any part of the system",
      "Modify, reverse engineer, or interfere with system functionality",
      "Upload harmful, inappropriate, or misleading content",
      "Use RallyPointGo for harassment, misconduct, or any non-team-related purpose",
      "Circumvent enrollment, approval, or notification processes",
    ],
    outro: "Violations may result in suspension or termination of access.",
  },
  {
    heading: "Privacy and Data Handling",
    text: "Personal information entered into RallyPointGo is used solely for team operations, communication, and safety. Users acknowledge that coaches and authorized personnel may view profile information, contact details, and team-related activity. RallyPointGo does not sell or distribute user data to third parties.",
  },
  {
    heading: "System Modifications",
    text: "RallyPointGo may update, modify, or discontinue features, workflows, or policies at any time without prior notice. Continued use of the system constitutes acceptance of any such changes.",
  },
  {
    heading: "Termination of Access",
    text: "RallyPointGo reserves the right to suspend or terminate user access for violations of these Terms of Use, misuse of the system, or actions that compromise security, privacy, or team operations.",
  },
  {
    heading: "Governing Law",
    text: "These Terms of Use shall be governed by and interpreted in accordance with applicable laws in the jurisdiction where the system is administered.",
  },
];

export function TermsBody() {
  return (
    <div className="space-y-4 text-sm leading-relaxed text-slate-600" data-testid="terms-body">
      {SECTIONS.map((s) => (
        <div key={s.heading}>
          <h4 className="font-semibold text-slate-900">{s.heading}</h4>
          {s.text && <p className="mt-1">{s.text}</p>}
          {s.intro && <p className="mt-1">{s.intro}</p>}
          {s.bullets && (
            <ul className="mt-1 ml-5 list-disc space-y-0.5">
              {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
          {s.outro && <p className="mt-1">{s.outro}</p>}
        </div>
      ))}
      <p className="pt-2 text-xs text-slate-400">© 2026 Joseph Johnson. All rights reserved.</p>
    </div>
  );
}

export function LiabilityBody() {
  return (
    <div className="space-y-4 text-sm leading-relaxed text-slate-600" data-testid="liability-body">
      <div>
        <h4 className="font-semibold text-slate-900">Limitation of Liability</h4>
        <p className="mt-1">To the fullest extent permitted by applicable law, RallyPointGo, its developer (Joseph Johnson), its administrators, and any affiliated personnel shall not be liable for any direct, indirect, incidental, consequential, special, exemplary, or punitive damages arising out of or relating to the use of, inability to use, or reliance upon the RallyPointGo system or any associated documentation or materials. This includes, without limitation, damages for loss of data, loss of access, device or network failures, missed notifications, scheduling conflicts, operational decisions, or any other losses resulting from user actions, omissions, or technical issues.</p>
      </div>
      <div>
        <p>Users acknowledge and agree that:</p>
        <ul className="mt-1 ml-5 list-disc space-y-0.5">
          <li>All information entered into RallyPointGo is the sole responsibility of the submitting user.</li>
          <li>Coaches, directors, and team members are responsible for verifying the accuracy of all data displayed within the system.</li>
          <li>RallyPointGo does not guarantee uninterrupted service, error-free operation, or the accuracy of system-generated information.</li>
          <li>Access issues resulting from forgotten passwords, unapproved enrollment requests, disabled notifications, or device configuration errors are not the responsibility of RallyPointGo or its developer.</li>
          <li>Any decisions made based on information displayed in RallyPointGo are made at the user's own discretion and risk.</li>
        </ul>
      </div>
      <p>In no event shall RallyPointGo or its developer be liable for any claims, damages, or losses exceeding the amount paid (if any) for use of the system. Use of RallyPointGo constitutes acceptance of this limitation of liability.</p>
      <p className="pt-2 text-xs text-slate-400">© 2026 Joseph Johnson. All rights reserved.</p>
    </div>
  );
}

export function LiabilityDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="liability-view-btn">
          <ShieldAlert className="mr-2 h-4 w-4" /> View Liability Limitation Clause
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="liability-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">Limitation of Liability</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-2"><LiabilityBody /></div>
      </DialogContent>
    </Dialog>
  );
}

export default function TermsDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="terms-view-btn">
          <ScrollText className="mr-2 h-4 w-4" /> View Terms of Use
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="terms-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">Terms of Use</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-2"><TermsBody /></div>
      </DialogContent>
    </Dialog>
  );
}
